import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import gql from 'graphql-tag';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import formatString from 'format-string-by-pattern';
import { useNotifications } from 'modules/notification';
import { DialogHeader } from 'components/shared/Dialog';
import { CommonDataContext } from 'contexts/CommonDataContext';
import { buildTree, getDropdownTreeSelectDefaultOptions } from 'services/locationsService';
import LocationsTreeViewDropdown from 'components/LocationsTreeViewDropdown/LocationsTreeViewDropdown';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';

const SIGNUP_USER_MUTATION = gql`
  mutation (
    $email: String!
    $firstName: String
    $lastName: String
    $title: String
    $phone: String
    $dynamicsAccountId: String
    $accountId: String
    $vendorContactRole: Int
    $locationId: ID
    $sendInvitation: Boolean
  ) {
    adminCreateUser(
      input: {
        email: $email
        firstName: $firstName
        lastName: $lastName
        title: $title
        phone: $phone
        dynamicsAccountId: $dynamicsAccountId
        accountId: $accountId
        vendorContactRole: $vendorContactRole
        locationId: $locationId
        sendInvitation: $sendInvitation
      }
    ) {
      status
      message
      userId
    }
  }
`;

export const AddNewRepModal = ({ open, locations, handleClose, refetchUsers }: any) => {
  const vendorProfile = useSelector((state: any) => state.vp);
  const account = useSelector((state: any) => state.account);

  const { showNotification } = useNotifications();

  const [requestSignupUser, { loading }] = useMutation(SIGNUP_USER_MUTATION, { context: { authRequired: false } });

  const [sendInvite, setSendInvite] = useState(true);
  const [locationsTree, setLocationsTree] = useState<any>();
  const { isDealerUser } = useContext(CommonDataContext);

  const [values, setValues] = useState({
    title: null,
    email: null,
    phone: null,
    lastName: null,
    firstName: null,
    locationId: null,
    vendorContactRole: null,
  });

  const handleCloseModal = () => {
    setValues({
      title: null,
      email: null,
      phone: null,
      lastName: null,
      firstName: null,
      locationId: null,
      vendorContactRole: null,
    });
    handleClose();
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      let phoneValue = value.replace(/[^\d-() ]/, '');
      setValues({
        ...values,
        [name]: formatString('(999) 999-9999', phoneValue),
      });
    } else {
      setValues({ ...values, [name]: value });
    }
  };

  useEffect(() => {
    if (_.isEmpty(locations)) return;

    const options = _.map(locations, location => {
      return {
        ...location,
        isDefaultValue: location.locationId === values.locationId,
      };
    });
    setLocationsTree(buildTree(options));
  }, [locations, values.locationId]);

  const handleSelectChange = (e: any) => {
    setValues({ ...values, vendorContactRole: e.target.value });
  };

  const treeSelectOptions = getDropdownTreeSelectDefaultOptions({
    onChange: (selectedNode: any) => setValues({ ...values, locationId: selectedNode.value }),
    texts: { placeholder: 'Default Location' },
  });

  const { locationId, ...requiredFields } = values;
  const isFormValid = Object.values(requiredFields).every(Boolean);

  const handleSubmit = async (event: any) => {
    try {
      event.preventDefault();

      const { data } = await requestSignupUser({
        variables: {
          mobilePhone: null,
          accountId: account.id,
          title: values['title'],
          phone: values['phone'],
          email: values['email'],
          sendInvitation: sendInvite,
          lastName: values['lastName'],
          locationId: values.locationId,
          firstName: values['firstName'],
          dynamicsAccountId: account.dynamicsAccountId,
          vendorContactRole: values['vendorContactRole'],
        },
      });

      if (data?.adminCreateUser?.status) {
        showNotification('New User Registered!');
        refetchUsers();
        handleCloseModal();
      } else {
        showNotification('Create New Rep error!', { type: 'error' });
      }
    } catch (error) {
      showNotification('Create New Rep error!', { type: 'error' });
    }
  };

  return (
    <Dialog onClose={handleCloseModal} open={open} fullWidth maxWidth="md">
      <DialogHeader label="Add New User" handleClose={handleCloseModal} />

      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingTop: '0.5rem' }}>
            <Stack direction="row" gap="1rem">
              <TextField
                required
                fullWidth
                size="small"
                name="firstName"
                variant="outlined"
                label="First Name"
                onChange={handleInputChange}
              />

              <TextField
                required
                fullWidth
                size="small"
                name="lastName"
                variant="outlined"
                label="Last Name"
                onChange={handleInputChange}
              />
            </Stack>

            <TextField
              required
              fullWidth
              name="title"
              size="small"
              label="Title"
              variant="outlined"
              onChange={handleInputChange}
            />

            <TextField
              required
              fullWidth
              size="small"
              type="email"
              name="email"
              variant="outlined"
              label="Email Address"
              onChange={handleInputChange}
            />

            {isDealerUser && (
              <FormControl size="small" fullWidth required>
                <InputLabel variant="outlined" id="vendor-contact-role-label">
                  User Role
                </InputLabel>

                <Select
                  variant="outlined"
                  id="vendorContactRole"
                  label="Trnsact User Role"
                  labelId="vendor-contact-role-label"
                  onChange={handleSelectChange}
                >
                  <MenuItem value={100000000}>Sales Rep</MenuItem>
                  <MenuItem value={100000001}>Sales Manager</MenuItem>
                  <MenuItem value={100000002}>System Admin</MenuItem>
                  <MenuItem value={100000003}>Executive</MenuItem>
                  <MenuItem value={100000004}>Credit Manager</MenuItem>
                  <MenuItem value={100000005}>Support Admin</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              required
              fullWidth
              size="small"
              name="phone"
              variant="outlined"
              value={values.phone}
              label="Phone Number"
              onChange={handleInputChange}
            />

            {vendorProfile?.multiLocationManagement && (
              <FormControl fullWidth>
                <LocationsTreeViewDropdown tree={locationsTree} treeSelectOptions={treeSelectOptions} />
              </FormControl>
            )}

            <Stack direction="row" width="100%" justifyContent="center">
              <FormControlLabel
                label={sendInvite ? 'Send Invitation' : 'Stage Invitation'}
                control={<Switch value={sendInvite} defaultChecked onChange={() => setSendInvite(!sendInvite)} />}
              />
            </Stack>
          </Box>

          <Stack direction="row" width="100%" justifyContent="center" sx={{ marginTop: '1rem' }}>
            <Tooltip title={!isFormValid ? 'Please fill in the required fields' : ''}>
              <span>
                <Button
                  type="submit"
                  loading={loading}
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!isFormValid}
                >
                  Register User
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};
